/**
 * ScenarioResults Component
 *
 * Displays FTE recommendations from scenario modeling
 */

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { ScenarioResults as ScenarioResultsType } from '../types/scenario.types';

interface ScenarioResultsProps {
  results: ScenarioResultsType;
}

export default function ScenarioResults({ results }: ScenarioResultsProps) {
  const roles = Object.keys(results.requiredFTE);

  const getTotalDelta = () => {
    return Object.values(results.delta).reduce((sum, d) => sum + d, 0);
  };

  const totalDelta = getTotalDelta();

  return (
    <Box>
      {/* Summary Alert */}
      <Alert
        severity={totalDelta > 0.25 ? 'warning' : totalDelta < -0.25 ? 'info' : 'success'}
        sx={{ mb: 3 }}
      >
        {totalDelta > 0.25 && (
          <>
            <strong>Additional staffing needed:</strong> {totalDelta.toFixed(1)} FTE total to meet
            target utilization
          </>
        )}
        {totalDelta < -0.25 && (
          <>
            <strong>Staffing surplus:</strong> {Math.abs(totalDelta).toFixed(1)} FTE over target
          </>
        )}
        {Math.abs(totalDelta) <= 0.25 && <strong>Current staffing matches target utilization</strong>}
      </Alert>

      {/* FTE Comparison Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">FTE Requirements by Role</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell align="right">Current FTE</TableCell>
                <TableCell align="right">Required FTE</TableCell>
                <TableCell align="right">Delta</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => {
                const delta = results.delta[role];
                const current = results.currentFTE[role] || 0;
                const required = results.requiredFTE[role];

                return (
                  <TableRow key={role}>
                    <TableCell>
                      <strong>{role}</strong>
                    </TableCell>
                    <TableCell align="right">{current.toFixed(1)}</TableCell>
                    <TableCell align="right">{required.toFixed(1)}</TableCell>
                    <TableCell align="right">
                      {delta > 0 ? '+' : ''}
                      {delta.toFixed(1)}
                    </TableCell>
                    <TableCell align="center">
                      {delta > 0.25 && <Chip label="Need More" color="warning" size="small" />}
                      {delta < -0.25 && <Chip label="Surplus" color="info" size="small" />}
                      {Math.abs(delta) <= 0.25 && <Chip label="Balanced" color="success" size="small" />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Monthly Projection */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Monthly Projection</Typography>
          <Typography variant="caption" color="text.secondary">
            Shows required FTE over time with growth factored in
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Patient Volume</TableCell>
                {roles.map((role) => (
                  <TableCell key={role} align="right">
                    {role} FTE
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.projectedMonthly.map((projection) => (
                <TableRow key={projection.month}>
                  <TableCell>{projection.monthLabel}</TableCell>
                  <TableCell align="right">{projection.patientVolume}</TableCell>
                  {roles.map((role) => (
                    <TableCell key={role} align="right">
                      {projection.requiredFTE[role]?.toFixed(1) || '0.0'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer Note */}
      <Alert severity="info" sx={{ mt: 3 }}>
        These projections are simulations based on the parameters you provided. They do not modify
        any staffing data.
      </Alert>
    </Box>
  );
}
